/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, type Type, Component, Injector, Input, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import {
    type ActionAbstract, type Command, type HttpResponse, type JobResponse,
    type PromptConfigFull,
    type Rest, DMValidators, ExtendService, FormControl, FormGroup, FormSlideWindowAbstract,
    SelectComponent, ToastService, UNPLACEHOLDER, UtilService,
} from '@dm/core';
import { FileSystemModeE } from 'app/common/constants';
import { SmartGuiService, TaskService, ToolService } from 'app/common/services';
import { type CIFSClient, type CIFSShare, type Dtree, type FileSystem, type Omtask } from 'app/common/types/mo';
import { cloneDeep } from 'lodash';
import { type Subscription, Observable, of } from 'rxjs';
import { catchError, finalize, flatMap, map, mergeMap, tap } from 'rxjs/operators';

import { CreateFileSystemComponent, FileSystemEntryE } from '../../../file-system/create-file-system/create-file-system.component';
import { FsSmartGuiInfoComponent } from '../../../file-system/fs-smartgui-info/fs-smartgui-info.component';
import { type CIFSShareVm, type CreateCifsParams, type SmartguiCifsshareReceived } from '../../share';
import { type AddCifsClientParams, CifsShareService } from '../cifs-share.service';
import { CifsShareStrService, OffLineFileModeE } from '../cifs-share-str.service';
import { ClientCifsShareService } from '../client-cifs-share/client-cifs-share.service';
import { DtreeService } from './../../../dtree/dtree.service';

@Component({
    selector: 'app-common-create-cifs-share',
    templateUrl: './create-cifs-share.component.html',
    styleUrls: ['./create-cifs-share.component.scss'],
})
export class CreateCifsShareComponent extends FormSlideWindowAbstract implements OnInit {
    i18n = this.str.i18n;
    @Input() fileSystem: FileSystem = {};
    @Input() dtree: Dtree = {};
    @Input() vstoreId: string;
    @Input() vstoreName: string;
    // 模板创建的共享
    @Input() createAsShare: CIFSShare;
    // 是否支持智能GUI，默认支持，当前copyfrom createas 的页面不支持
    @Input() supportSmartGui = true;

    /**
     * copy from的方式，有new 和copyfrom两种。如果是copy from过来的，必填字段
     */
    @Input() copyFromStyle: 'new' | 'copyFrom';
    // copy from 过来的title是要替换的
    @Input() title: string = this.i18n.title.createTitle;
    /**
     * copy from传过来的share属性
     */
    @Input() copyFromShare: CIFSShare;
    /**
     * copy from传过来的clients列表,new 和 copyfrom模式下都有，第一次copyfrom过来的是undefined
     */
    @Input() copyFromPermissions: CIFSClient[];

    @Input() entry: FileSystemEntryE;

    @ViewChild('fsSelect') fsSelect: SelectComponent;

    OffLineFileModeE = OffLineFileModeE;
    UNPLACEHOLDER = UNPLACEHOLDER;

    closeArgs = {
        refresh: false,
        data: null,
    };

    /**
     * 侧划框忙等
     */
    pathLoading = false;

    loading = false;

    vm: FormGroup;
    errorMessage: any;

    get vmValue(): CIFSShareVm {
        return this.vm ? this.vm.value : null;
    }

    fileSystemRest: Rest;
    dtreeRest: Rest;

    // 共享路径
    private _share_path = '';

    set share_path(value) {
        this._share_path = value;
        // 更新是否有\
        this.dtreePathHasBackslash = this.isDtreePathHasBackslash();
    }

    get share_path(): string {
        // 如果是copyfrom过来的，sharepath 就固定从copyfrom share中获取，不会变动。
        // 这么写是为了防止share_path被其他逻辑给赋值了，避免了路径为空的时候显示 --
        return this.copyFromStyle ? this.copyFromShare.SHAREPATH : this._share_path;
    }

    /**
     * dtree path 中是否含有反斜杠
     */
    dtreePathHasBackslash = false;
    // 手动选择的文件系统对应的租户名
    selectVstoreName = '';
    // CIFS用户列表
    userList: CIFSClient[] = [];
    // 一次查询用户的分页数量，只有copy from 的第一次打开侧划框才有用
    maxUsersRange = 100;
    // 查询全量users的忙等
    allUserLoading = false;
    // 是否允许更改文件系统
    canFileSystemChange = true;
    // 是否允许更改dtree
    canDtreeChange = true;
    // 是否显示dtree
    showDtree = true;
    /** 是否有Dtree读权限，没有则不能选择Dtree */
    hasDtreePermission = this.permissionService.hasPermission('dtree_R');

    selectedFilesystem: FileSystem;

    /**
     * 当前共享是否基于审计日志文件系统
     */
    baseOnAuditLogFs: boolean;

    advanced = false;
    // 上一次选中的文件系统所属租户的vstoreId
    preFsVstoreId: string;
    // 创建文件系统按钮灰化标识
    createFsDisabled = false;

    // 从接口获取的NFS智能推荐数据
    cifsSmartguiData: SmartguiCifsshareReceived = {};

    createFsTaskId: string;
    createFs$: Subscription;

    constructor(
        injector: Injector,
        public str: CifsShareStrService,
        private CIFSService: CifsShareService,
        private clientService: ClientCifsShareService,
        private toolService: ToolService,
        private dtreeService: DtreeService,
        private smartguiService: SmartGuiService,
        private extend: ExtendService,
        private utilService: UtilService,
        private toastService: ToastService,
        private taskService: TaskService,

    ) {
        super(injector);
    }

    ngOnInit(): void {
        if (!this.checkDepPermission(['file_system_R'])) {
            return;
        }
        if (this.copyFromShare) {
            this.share_path = this.copyFromShare.SHAREPATH;
        }
        // 如果是new，并且传入了Users列表，就直接赋值
        if (this.copyFromStyle === 'new' && this.copyFromPermissions) {
            // 深度拷贝一把，不然修改User之后，没有点确定，Users的数据依然被更改了。
            this.userList = cloneDeep(this.copyFromPermissions);
        } else if (this.copyFromStyle === 'copyFrom' && !this.copyFromPermissions) {
            // copyFrom模式下copyFromPermissions如果是undefined，那就说明是第一次点开，才需要去查询全量的Users
            this.getAllUsers(this.copyFromShare);
        } else if (this.copyFromStyle === 'copyFrom' && this.copyFromPermissions) {
            // copyFrom模式下copyFromPermissions如果是数组，那就说明是已经查询过全量数据了，就不需要查询了,直接赋值
            this.userList = cloneDeep(this.copyFromPermissions);
        }

        // 如果是模板创建，也需要查询所有的客户端数据
        if (this.createAsShare) {
            this.getAllUsers(this.createAsShare);
        }

        this.entryHandler();
        this.fileSystemRest = this.CIFSService.getBatchFSRest(this.vstoreId);
        if (this.vstoreId) {
            this.preFsVstoreId = this.vstoreId;
        }
        super.ngOnInit();

        this.pageReady = true;
        this.windowVisible = true;

        // 如果不是从copyFrom和createas打开的则获取nfs智能推荐数据, 从copyFrom打开copyFromStyle一定有值
        if (this.supportSmartGui && !this.copyFromStyle) {
            this.getCifsSmartguiData();
        }
    }

    getAllUsers(share: CIFSShare): void {
        this.allUserLoading = true;
        this.clientService.getCount(share.ID, share.vstoreId)
            .pipe(
                flatMap(({ data }) => {
                    const count = Number(data.COUNT);
                    if (count === 0) {
                        return [];
                    }
                    // 计算重复查询次数
                    const times = Math.floor(count / this.maxUsersRange) + (count % this.maxUsersRange === 0 ? 0 : 1);
                    const cmds: Observable<HttpResponse>[] = [];
                    // 循环构造所有的查询接口
                    for (let i = 0; i < times; i++) {
                        cmds.push(
                            this.clientService.getBatch(share.ID, share.vstoreId,
                                [i * this.maxUsersRange, (i + 1) * this.maxUsersRange]),
                        );
                    }
                    return this.extend.forkOrder(cmds);
                }),
                tap((results) => {
                    const datas: CIFSClient[] = [];
                    results.forEach((result) => {
                        if (Array.isArray(result.data)) {
                            datas.push(...result.data);
                        }
                    });
                    // 拿到数据之后，还要处理一下
                    this.userList = datas;
                }),
                finalize(() => {
                    this.allUserLoading = false;
                }),
            ).subscribe();
    }

    /**
     * 获取CIFS智能推荐数据
     */
    getCifsSmartguiData(): void {
        if (!this.smartguiService.switch) {
            return;
        }

        // 添加忙等效果
        this.showLoading();
        this.CIFSService.getSmartguiCIFS(this.vstoreId, { requestTimeoutTip: this.i18n.error.smartRecommendDataTimeoutMsg })
            .pipe(
                flatMap(({ data }) => {
                    this.cifsSmartguiData = {
                        ...this.cifsSmartguiData,
                        ...data,
                    };
                    this.vm.get('NAME').setValue(this.cifsSmartguiData.NAME[0]);
                    if (this.cifsSmartguiData.ENABLENOTIFY) {
                        this.vm.get('ENABLENOTIFY').setValue(this.cifsSmartguiData.ENABLENOTIFY === 'true');
                    }
                    if (this.cifsSmartguiData.ENABLECA) {
                        this.vm.get('ENABLECA').setValue(this.cifsSmartguiData.ENABLECA === 'true');
                    }
                    if (this.cifsSmartguiData.smb3encryptionenable) {
                        this.vm.get('smb3EncryptionEnable').setValue(this.cifsSmartguiData.smb3encryptionenable === 'true');
                    }
                    if (this.cifsSmartguiData.unencryptedaccess) {
                        this.vm.get('unencryptedAccess').setValue(this.cifsSmartguiData.unencryptedaccess === 'true');
                    }
                    if (this.cifsSmartguiData.ABEENABLE) {
                        this.vm.get('ABEENABLE').setValue(this.cifsSmartguiData.ABEENABLE === 'true');
                    }
                    if (this.cifsSmartguiData.ENABLESHOWSNAPSHOT) {
                        this.vm.get('ENABLESHOWSNAPSHOT').setValue(this.cifsSmartguiData.ENABLESHOWSNAPSHOT === 'true');
                    }
                    if (this.cifsSmartguiData.ENABLESHOWPREVIOUSVERSIONS) {
                        this.vm.get('ENABLESHOWPREVIOUSVERSIONS').setValue(this.cifsSmartguiData.ENABLESHOWPREVIOUSVERSIONS === 'true');
                    }
                    if (this.cifsSmartguiData.OFFLINEFILEMODE) {
                        this.vm.get('OFFLINEFILEMODE').setValue(Number(this.cifsSmartguiData.OFFLINEFILEMODE));
                    }
                    if (this.cifsSmartguiData && this.cifsSmartguiData.filesystem) {
                        const queryParams = {
                            name: this.cifsSmartguiData.filesystem,
                            vstoreId: this.vstoreId,
                        };
                        return this.CIFSService.getFSOnName(queryParams);
                    } else {
                        return of(null);
                    }
                }),
                finalize(() => this.hideLoading()),
            )
            .subscribe((resp: HttpResponse | null) => {
                if (resp?.data.length && this.fileSystem && !this.fileSystem.NAME) {
                    const { data: [item] } = resp;
                    this.fsSelect.setFixedOption({
                        value: item && item.ID,
                        label: item && item.NAME,
                        data: item,
                    });
                }
            });
    }

    /**
     * 入口的判断和处理
     */
    entryHandler(): void {
        // 如果传入了文件系统，就证明是从文件系统或dtree入口进来的，此时不允许修改文件系统和dtree
        if (this.fileSystem?.NAME) {
            this.share_path = `/${this.fileSystem.NAME}/`;
            this.canFileSystemChange = false;
            this.selectedFilesystem = this.fileSystem;
            this.baseOnAuditLogFs = this.selectedFilesystem.isAuditLogFs;

            if (this.dtree?.NAME) {
                this.pathLoading = true;
                this.canDtreeChange = false;
                this.CIFSService.getDtree(this.dtree.ID, this.vstoreId).pipe(
                    finalize(() => {
                        this.pathLoading = false;
                    }),
                ).subscribe(({ data }: { data: Dtree }) => {
                    this.share_path = this.dtreeService.getDtreeFullPath(this.fileSystem, data);
                });
            } else {
                // 未传入有效Dtree说明是从文件系统详情进入，直接不显示Dtree
                this.showDtree = false;
            }

            // 审计日志文件系统、哈希文件系统和内部文件系统要限制共享权限
            if (this.fileSystem.isAuditLogFs || this.fileSystem.SUBTYPE === FileSystemModeE.SMART_MOBILITY_INNER_FILE_SYSTEM ||
                this.selectedFilesystem.SUBTYPE === FileSystemModeE.HASH) {
                this.entry = FileSystemEntryE.AuditLog;
            }
            // 如果是模板创建共享
        } else if (this.createAsShare && this.createAsShare.FSID) {
            this.getAuditLogFsInfo(this.createAsShare.FSID);
        }
    }

    /**
     * 判断是否有\ \要转义才能判断
     */
    isDtreePathHasBackslash(): boolean {
        return this.share_path.indexOf('\\') !== -1;
    }

    /**
     * 复写合法性校验，增加依赖权限控制
     */
    validate(): Observable<boolean> {
        return this.vm.validateOb()
            .pipe(
                mergeMap((validate: boolean) => {
                    if (!validate) {
                        return of(false);
                    }
                    // 权限校验不通过会触发error
                    const checkPermission: Observable<boolean> = this.permissionService.checkDepPermission(['task_W'])
                        .pipe(map(() => true));
                    // 选了用户/用户组走omtask创共享，需要检查task写权限
                    return this.userList.length ? checkPermission : of(true);
                }),
                catchError(() =>
                    // 校验不通过
                    of(false),
                ),
            );
    }

    /**
     * 复写modify方法，传入文件系统时，需要直接true 让buldcmd执行
     */
    isModified(): boolean {
        if (this.fileSystem && this.fileSystem.NAME) {
            return true;
        }
        return this.vm.valueChanged;
    }

    initVm(): void {
        // 初始化默认数据
        const defaultShare: CIFSShare = {
            NAME: this.copyFromShare ? this.copyFromShare.NAME : '',
            FS: this.selectedFilesystem,
            DESCRIPTION: this.copyFromStyle ? this.copyFromShare.DESCRIPTION : '',
            ENABLENOTIFY: this.copyFromStyle ? this.copyFromShare.ENABLENOTIFY : 'true',
            ENABLECA: this.copyFromStyle ? this.copyFromShare.ENABLECA : 'true',
            ABEENABLE: this.copyFromStyle ? this.copyFromShare.ABEENABLE : 'false',
            ENABLESHOWSNAPSHOT: this.copyFromStyle ? this.copyFromShare.ENABLESHOWSNAPSHOT : 'true',
            smb3EncryptionEnable: this.copyFromStyle ? this.copyFromShare.smb3EncryptionEnable : 'false',
            unencryptedAccess: this.copyFromStyle ? this.copyFromShare.unencryptedAccess : 'false',
            ENABLESHOWPREVIOUSVERSIONS: this.copyFromStyle ? this.copyFromShare.ENABLESHOWPREVIOUSVERSIONS : 'true',
            OFFLINEFILEMODE: this.copyFromStyle ? this.copyFromShare.OFFLINEFILEMODE : OffLineFileModeE.None,
        };
        // 更新模板创建的数据
        if (this.createAsShare) {
            defaultShare.DESCRIPTION = this.createAsShare.DESCRIPTION;
            defaultShare.ENABLENOTIFY = this.createAsShare.ENABLENOTIFY;
            defaultShare.ENABLECA = this.createAsShare.ENABLECA;
            defaultShare.smb3EncryptionEnable = this.createAsShare.smb3EncryptionEnable;
            defaultShare.unencryptedAccess = this.createAsShare.unencryptedAccess;
            defaultShare.ABEENABLE = this.createAsShare.ABEENABLE;
            defaultShare.ENABLESHOWSNAPSHOT = this.createAsShare.ENABLESHOWSNAPSHOT;
            defaultShare.ENABLESHOWPREVIOUSVERSIONS = this.createAsShare.ENABLESHOWPREVIOUSVERSIONS;
            defaultShare.OFFLINEFILEMODE = this.createAsShare.OFFLINEFILEMODE;
        }

        this.vm = new FormGroup({
            NAME: new FormControl(defaultShare.NAME, {
                validators: [
                    Validators.required,
                    DMValidators.rangeByteLength(1, 80),
                    this.CIFSService.cifsShareNameValidator,
                ],
                updateOn: 'blur',
            }),
            FS: new FormControl(defaultShare.FS, [
                Validators.required,
            ]),
            dtree: new FormControl(null, [
            ]),
            DESCRIPTION: new FormControl(this.utilService.decodeHTML(defaultShare.DESCRIPTION), [
                DMValidators.rangeByteLength(0, 255),
            ]),
            ENABLENOTIFY: new FormControl(typeof defaultShare.ENABLENOTIFY === 'string' ?
                defaultShare.ENABLENOTIFY === 'true' :
                defaultShare.ENABLENOTIFY),
            ENABLECA: new FormControl(typeof defaultShare.ENABLECA === 'string' ?
                defaultShare.ENABLECA === 'true' :
                defaultShare.ENABLECA),
            smb3EncryptionEnable: new FormControl(typeof defaultShare.smb3EncryptionEnable === 'string' ?
                defaultShare.smb3EncryptionEnable === 'true' :
                defaultShare.smb3EncryptionEnable),
            unencryptedAccess: new FormControl(typeof defaultShare.unencryptedAccess === 'string' ?
                defaultShare.unencryptedAccess === 'true' :
                defaultShare.unencryptedAccess),
            ABEENABLE: new FormControl(typeof defaultShare.ABEENABLE === 'string' ?
                defaultShare.ABEENABLE === 'true' :
                defaultShare.ABEENABLE),
            ENABLESHOWSNAPSHOT: new FormControl(typeof defaultShare.ENABLESHOWSNAPSHOT === 'string' ?
                defaultShare.ENABLESHOWSNAPSHOT === 'true' :
                defaultShare.ENABLESHOWSNAPSHOT),
            ENABLESHOWPREVIOUSVERSIONS: new FormControl(typeof defaultShare.ENABLESHOWPREVIOUSVERSIONS === 'string' ?
                defaultShare.ENABLESHOWPREVIOUSVERSIONS === 'true' :
                defaultShare.ENABLESHOWPREVIOUSVERSIONS),
            OFFLINEFILEMODE: new FormControl(defaultShare.OFFLINEFILEMODE),
        }, { updateOn: 'blur' });

        this.errorMessage = {
            NAME: {
                required: this.toolService.requiredErr(this.i18n.label.share_name),
                rangeByteLength: this.toolService.rangeByteLengthErr(1, 80),
                cifsShareName: this.str.get(this.i18n.tip.shareNameValidation,
                    [this.i18n.label.name]),
            },
            FS: {
                required: this.toolService.requiredErr(this.i18n.label.fileSystem),
            },
            DESCRIPTION: {
                rangeByteLength: this.toolService.rangeByteLengthErr(0, 255),
            },
        };
    }

    /**
     * 收集copyfromm页面需要的信息
     */
    collectionCIFSAndUsers(): { share: CIFSShare; permissions: CIFSClient[] } {
        const vmValue = this.vmValue;
        const share: CIFSShare = {
            NAME: vmValue.NAME,
            DESCRIPTION: vmValue.DESCRIPTION,
            SHAREPATH: this.share_path,
            ENABLENOTIFY: vmValue.ENABLENOTIFY,
            ENABLECA: vmValue.ENABLECA,
            ABEENABLE: vmValue.ABEENABLE,
            ENABLESHOWSNAPSHOT: vmValue.ENABLESHOWSNAPSHOT,
            smb3EncryptionEnable: vmValue.smb3EncryptionEnable,
            unencryptedAccess: vmValue.unencryptedAccess,
            ENABLESHOWPREVIOUSVERSIONS: vmValue.ENABLESHOWPREVIOUSVERSIONS,
            OFFLINEFILEMODE: vmValue.OFFLINEFILEMODE,
        };
        return { share, permissions: this.userList };
    }

    buildCmds(): Command[] {
        const cmds: Command[] = [];
        if (this.copyFromStyle) {
            this.closeArgs.data = this.collectionCIFSAndUsers();
        } else {
            if (this.userList.length) {
                // 走omtask
                cmds.push(
                    this.buildCIFSCmdByOmtask(),
                );
            } else {
                cmds.push(
                    this.buildCIFSCmd(),
                );
            }
        }
        return cmds;
    }

    prompt(): PromptConfigFull[] {
        const prompts: PromptConfigFull[] = [];
        if (!this.copyFromStyle) {
            const title = this.createAsShare ? this.i18n.title.createAsTitle : this.title;
            if (this.vm.get('smb3EncryptionEnable').value) {
                prompts.push({
                    id: 'prompt-create-cifs-share-SMB3EncryptEnabledTip',
                    opType: 'warning',
                    title,
                    content: this.i18n.tip.SMB3EncryptEnabledTip,
                });
            }
            if (this.vm.get('unencryptedAccess').value) {
                prompts.push({
                    id: 'prompt-create-cifs-share-enableUnencryptedAccessTip',
                    opType: 'warning',
                    title,
                    content: this.i18n.tip.enableUnencryptedAccessTip,
                });
            }

            if (this.vm.get('OFFLINEFILEMODE').value !== OffLineFileModeE.None) {
                prompts.push({
                    id: 'prompt-cifs-share-modeNotNoneTip',
                    opType: 'info',
                    title,
                    content: this.i18n.tip.modeNotNoneTip,
                });
            }
        }

        return prompts;
    }

    /**
     * 选择文件系统后，需要更新dtree和sharepath
     */
    onChooseFileSystem($event): void {
        if (!$event.value) {
            this.share_path = '';
            return;
        }
        this.share_path = `/${$event.option.data.NAME}/`;

        this.selectedFilesystem = $event.option.data;

        if (this.hasDtreePermission) {
            this.dtreeRest = this.CIFSService.getBatchDtreeRest(this.selectedFilesystem);
        }

        // 清空dtree
        this.vm.get('dtree').setValue(null);

        const currentFsVstoreId = this.selectedFilesystem.vstoreId;

        // 如果切换的文件系统所属租户变更，清空已选权限列表
        if (currentFsVstoreId !== this.preFsVstoreId) {
            this.userList = [];
        }

        this.preFsVstoreId = currentFsVstoreId;

        // 审计日志文件系统、哈希文件系统和内部文件系统要限制共享权限,同时清空已选的权限用户/用户组, 如果本身是基于审计日志文件的共享，不清空
        if ((!this.baseOnAuditLogFs && this.selectedFilesystem.isAuditLogFs) ||
            this.selectedFilesystem.SUBTYPE === FileSystemModeE.SMART_MOBILITY_INNER_FILE_SYSTEM ||
            this.selectedFilesystem.SUBTYPE === FileSystemModeE.HASH) {
            this.entry = FileSystemEntryE.AuditLog;
            if (this.userList.length) {
                // 清空权限给个提示
                if (this.selectedFilesystem.isAuditLogFs) {
                    this.toastService.warning(this.i18n.tip.permissionClearTip);
                }
                this.userList = [];
            }
        } else {
            // 如果本身就是审计日志文件系统创建的共享再模板创建共享入口&选择是审计日志文件系统，entry设置为审计日志入口
            if (this.baseOnAuditLogFs && this.selectedFilesystem.isAuditLogFs) {
                this.entry = FileSystemEntryE.AuditLog;
            } else {
                this.entry = undefined;
            }
        }
    }

    /**
     * 获取单个文件系统详情，判断是否是审计日志文件系统
     */
    getAuditLogFsInfo(id: string): void {
        // 支持根目录的需求DM暂时未做，这里先规避没有文件系统的情况
        if (id !== '--') {
            this.CIFSService.getFileSystem(id, this.vstoreId).subscribe((data: FileSystem) => {
                this.baseOnAuditLogFs = data.isAuditLogFs;
            });
        }
    }

    /**
     * 选择dtree之后，需要更新sharepath
     */
    onChooseDtree($event): void {
        if (!$event.value) {
            this.share_path = `/${this.selectedFilesystem.NAME}/`;
            return;
        }
        // dtree的全路径是单个查的，这个时候要展示微观忙等
        this.pathLoading = true;
        this.CIFSService.getDtree($event.option.data.ID, this.selectedFilesystem.vstoreId).pipe(
            finalize(() => {
                this.pathLoading = false;
            }),
        ).subscribe(({ data }: { data: Dtree }) => {
            this.share_path = this.dtreeService.getDtreeFullPath(this.selectedFilesystem, data);
        });
    }

    /**
     * 创建文件系统
     */
    createFs(): void {
        this.createFsDisabled = true;

        let targetComponent: Type<ActionAbstract>;
        targetComponent = this.smartguiService.switch ? FsSmartGuiInfoComponent : CreateFileSystemComponent;
        this.doAction(targetComponent,
            {
                vstore: { label: this.vstoreName, value: this.vstoreId },
                onClose: ({ data }) => {
                    this.createFsDisabled = false;
                    if (!data) {
                        return;
                    }

                    if (data.NAME) {
                        this.addFsOptAndSelect(data);
                    } else if (data.taskId) {
                        this.initCreateFsTask(data);
                    }
                },
            },
        );
    }

    initCreateFsTask(data): void {
        this.createFsTaskId = data.taskId;
        if (this.createFs$) {
            this.createFs$.unsubscribe();
        }
        this.createFs$ = this.taskService.subscribe('fileSystem', (task, { id }: Omtask) => {
            if (id === this.createFsTaskId) {
                this.fsSelect.refresh();
            }
        });
    }

    /**
     * 文件系统下拉默认选中新建项
     * @param {*} data 后台返回的原始对象
     */
    addFsOptAndSelect(data: FileSystem): void {
        // 增加选项
        const newOption = {
            label: data.NAME,
            value: data.ID,
            data,
        };
        this.fsSelect.setFixedOption(newOption);
    }

    /**
     * 当处于所有租户模式并点击了权限列表的添加按钮，未选择文件系统时(无租户信息)给出必选校验提示
     */
    onClickAddBtn(): void {
        if (!this.vstoreId) {
            this.vm.get('FS').markAsTouched();
        }
    }

    /**
     * 构建创建共享的rest
     */
    buildCIFSCmd(): Command {
        const vmValue = this.vmValue;
        const params: CIFSShare = {
            NAME: vmValue.NAME,
            DESCRIPTION: vmValue.DESCRIPTION,
            SHAREPATH: this.share_path,
            ENABLENOTIFY: vmValue.ENABLENOTIFY,
            ENABLECA: vmValue.ENABLECA,
            ABEENABLE: vmValue.ABEENABLE,
            ENABLESHOWSNAPSHOT: vmValue.ENABLESHOWSNAPSHOT,
            smb3EncryptionEnable: vmValue.smb3EncryptionEnable,
            unencryptedAccess: vmValue.unencryptedAccess,
            ENABLESHOWPREVIOUSVERSIONS: vmValue.ENABLESHOWPREVIOUSVERSIONS,
            OFFLINEFILEMODE: vmValue.OFFLINEFILEMODE,
        };

        if (this.selectedFilesystem && this.selectedFilesystem.vstoreId) {
            params.vstoreId = this.selectedFilesystem.vstoreId;
        }

        return {
            id: 'cifs_share',
            name: this.i18n.rest.createCIFS,
            nameParams: [vmValue.NAME],
            action: this.CIFSService.create(params),
        };
    }

    /**
     * 构建走omtask创建共享的rest
     */
    buildCIFSCmdByOmtask(): Command {
        const vmValue = this.vmValue;
        const params: CreateCifsParams = {
            NAME: vmValue.NAME,
            DESCRIPTION: vmValue.DESCRIPTION,
            SHAREPATH: this.share_path,
            ENABLENOTIFY: vmValue.ENABLENOTIFY,
            ENABLECA: vmValue.ENABLECA,
            ABEENABLE: vmValue.ABEENABLE,
            ENABLESHOWSNAPSHOT: vmValue.ENABLESHOWSNAPSHOT,
            smb3EncryptionEnable: vmValue.smb3EncryptionEnable,
            unencryptedAccess: vmValue.unencryptedAccess,
            ENABLESHOWPREVIOUSVERSIONS: vmValue.ENABLESHOWPREVIOUSVERSIONS,
            OFFLINEFILEMODE: vmValue.OFFLINEFILEMODE,
        };

        if (this.selectedFilesystem?.vstoreId) {
            params.vstoreId = this.selectedFilesystem.vstoreId;
        }

        params.auth = this.buildCIFSClientParams();

        return {
            name: this.i18n.rest.createCIFS,
            nameParams: [vmValue.NAME],
            action: this.CIFSService.createByOmtask(vmValue.NAME, params),
        };
    }

    /**
     * 构建添加用户/用户组下发参数
     */
    buildCIFSClientParams(): CIFSClient[] {
        return this.userList.map((data) => ({
            NAME: data.NAME,
            DOMAINTYPE: Number(data.DOMAINTYPE),
            PERMISSION: Number(data.PERMISSION),
        }));
    }

    /**
     * 构建添加客户端（用户/用户组）的rest
     */
    buildCIFSClientCmd(): Command {
        const params: AddCifsClientParams = {};

        params.auth = this.userList.map((data) => ({
            NAME: data.NAME,
            DOMAINTYPE: Number(data.DOMAINTYPE),
            PERMISSION: Number(data.PERMISSION),
        }));

        // 没有客户端不创建任务
        if (!params.auth.length) {
            return null;
        }

        return {
            name: this.i18n.title.addCifsPermission,
            deps: ['cifs_share'],
            beforeAction: (shareRes): Observable<JobResponse> => {
                const { data } = shareRes;
                if (data && data.ID) {
                    params.cifsID = data.ID;
                    return Observable.create((observer) => {
                        observer.next();
                        observer.complete();
                    });
                } else {
                    return Observable.create((observer) => {
                        observer.error({
                            result: {
                                code: -1,
                            },
                        });
                    });
                }
            },
            action: (shareRes): Observable<JobResponse> => {
                const share: CIFSShare = shareRes.data;
                params.vstoreId = share.vstoreId;
                return this.clientService.addClients(share.NAME, params, {
                    showResult: false,
                    frontendDisplayResult: false,
                });
            },
        };
    }

    /**
     * 复写exec，如果走omtask，就不走cmd的流程。
     */
    exec(): void {
        // 不走omtask
        if (!this.userList.length) {
            super.exec();
            return;
        }

        // 走omtask
        const cmds = this.buildCmds();
        if (cmds && cmds.length === 1) {
            this.loading = true;
            (cmds[0].action as Observable<HttpResponse>).pipe(finalize(() => {
                this.loading = false;
            })).subscribe(() => {
                this.closeArgs.refresh = true;
                this.hide();
            });
        } else {
            this.hide();
        }
    }

    /**
     * 复写ok函数，当pathloading的时候，不让点
     */
    onOk(): void {
        if (this.pathLoading) {
            return;
        }
        super.onOk();
    }
}
